import React, { useEffect } from 'react';
import classNames from 'classnames/bind';
import { string } from 'prop-types';
import {
  params, shape, arrayOf, string as stringType, useDataModel
} from '@thd-nucleus/data-sources';
import { Heading } from '@thd-olt-component-react/heading';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import styles from './store-directory.module.scss';

const cx = classNames.bind(styles);

const StateDirectoryComponent = ({ state }) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('state-directory.ready'); }, []);

  const { data, loading, error } = useDataModel('storeDirectoryByState', {
    variables: { state }
  });

  const storesInfo = data?.storeDirectoryByState?.storesInfo || [];
  const stateName = data?.storeDirectoryByState?.stateName || '';

  if (loading) {
    return (
      <article className={cx('store-directory')}>
        <Heading />
        <LoadingPlaceholder
          color="#E0E0E0"
          ready={false}
          showLoadingAnimation
          style={{ height: 700 }}
          rows={20}
          type="text"
        ><div />
        </LoadingPlaceholder>
      </article>
    );
  }
  if (!storesInfo?.length || error) return null;

  return (
    <article className={cx('store-directory')}>
      <section className={cx('store-directory__header')}>
        <Heading title={`${stateName}`} underline />
      </section>
      <section className={cx('store-directory__state-wrapper')}>
        {storesInfo.map(({
          storeName,
          url,
          phone,
          rentalsLink,
          servicesLink,
          address
        }, storeIndex) => {
          return (
            <div className={cx('store-directory__store')} key={`${storeName}-${storeIndex}`}>
              <a
                className={cx('store-directory__store-link')}
                id={`${storeName}-${storeIndex}`}
                href={url}
              >
                {storeName}
              </a>

              <ul className={cx('store-directory__store-details')}>
                <li>{address.street}</li>
                <li>{`${address.city} ${address.state} ${address.postalCode}`}</li>
                <li>{phone}</li>
                <li>
                  <a
                    className={cx('store-directory__store-link')}
                    id={`${storeName}Rentals-${storeIndex}`}
                    href={rentalsLink}
                  >
                    {`${storeName} Rentals`}
                  </a>
                </li>
                <li>
                  <a
                    className={cx('store-directory__store-link')}
                    id={`${storeName}HomeServices-${storeIndex}`}
                    href={servicesLink}
                  >
                    {`${storeName} Home Services`}
                  </a>
                </li>
              </ul>
            </div>
          );
        })}
      </section>
    </article>
  );
};

const propTypes = {
  state: string.isRequired
};

StateDirectoryComponent.displayName = 'State Directory';

const dataModel = {
  storeDirectoryByState: params({ state: stringType().isRequired() }).shape({
    stateName: stringType(),
    storesInfo: arrayOf(shape({
      storeName: stringType(),
      url: stringType(),
      phone: stringType(),
      rentalsLink: stringType(),
      servicesLink: stringType(),
      address: shape({
        street: stringType(),
        city: stringType(),
        state: stringType(),
        postalCode: stringType(),
        county: stringType()
      })
    }))
  })
};
StateDirectoryComponent.dataModel = dataModel;
StateDirectoryComponent.propTypes = propTypes;

const HydratedComponent = withHydrator({
  delay: 500,
  id: 'state-directory',
  placeholder: (
    <LoadingPlaceholder
      color="#f5f5f5"
      ready={false}
      style={{ height: 750 }}
      type="rect"
    ><div />
    </LoadingPlaceholder>
  ),
  scrollBuffer: 100,
}, StateDirectoryComponent);

const StateDirectory = withDynamicComponent(HydratedComponent);

StateDirectory.dataModel = dataModel;
StateDirectory.propTypes = propTypes;

export { StateDirectory };